import {ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight} from 'lucide-react'

interface PaginationProps {
  currentPage: number
  totalPages: number
  totalItems: number
  itemsPerPage: number
  onPageChange: (page: number) => void
  onItemsPerPageChange: (count: number) => void
  disabled?: boolean
  itemLabel?: 'registros' | 'usuários'
}

const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  disabled = false,
  itemLabel = 'registros',
}: PaginationProps) => {
  const pageCount = Math.max(1, totalPages)
  const page = Math.max(1, Math.min(currentPage, pageCount))
  const startItem = totalItems === 0 ? 0 : (page - 1) * itemsPerPage + 1
  const endItem = Math.min(page * itemsPerPage, totalItems)
  const visiblePages = Array.from(
    new Set([
      1,
      pageCount,
      page - 1,
      page,
      page + 1,
      ...(page <= 3 ? [2, 3, 4, 5] : []),
      ...(page >= pageCount - 2
        ? [pageCount - 4, pageCount - 3, pageCount - 2, pageCount - 1]
        : []),
    ])
  )
    .filter((number) => number >= 1 && number <= pageCount)
    .sort((a, b) => a - b)
  const pages: Array<number | string> = []
  visiblePages.forEach((number, index) => {
    if (index && number - visiblePages[index - 1] > 1) pages.push(`gap-${number}`)
    pages.push(number)
  })
  const number = (value: number) => value.toLocaleString('pt-BR')

  return (
    <div className='logs-pagination'>
      <div className='logs-pagination-summary'>
        <label className='logs-page-size'>
          <span>Por página</span>
          <select
            aria-label={itemLabel === 'usuários' ? 'Usuários por página' : 'Registros por página'}
            value={itemsPerPage}
            disabled={disabled}
            onChange={(event) => onItemsPerPageChange(Number(event.target.value))}
          >
            {[10, 25, 50, 100].map((count) => (
              <option key={count} value={count}>
                {count}
              </option>
            ))}
          </select>
        </label>
        <span className='logs-pagination-range' aria-live='polite'>
          <span>
            {number(startItem)}–{number(endItem)}
          </span>{' '}
          de <strong>{number(totalItems)}</strong> {itemLabel}
        </span>
      </div>
      {totalPages > 1 && (
        <nav
          className='logs-pagination-nav'
          aria-label={
            itemLabel === 'usuários' ? 'Paginação dos usuários' : 'Paginação dos registros'
          }
        >
          <button
            type='button'
            className='logs-page-button'
            onClick={() => onPageChange(1)}
            disabled={disabled || page === 1}
            aria-label='Primeira página'
          >
            <ChevronsLeft size={16} aria-hidden='true' />
          </button>
          <button
            type='button'
            className='logs-page-button'
            onClick={() => onPageChange(page - 1)}
            disabled={disabled || page === 1}
            aria-label='Página anterior'
          >
            <ChevronLeft size={16} aria-hidden='true' />
          </button>
          {pages.map((item) =>
            typeof item === 'string' ? (
              <span key={item} className='logs-page-ellipsis' aria-hidden='true'>
                …
              </span>
            ) : (
              <button
                key={item}
                type='button'
                className={`logs-page-button${item === page ? ' is-active' : ''}`}
                onClick={() => onPageChange(item)}
                disabled={disabled}
                aria-current={item === page ? 'page' : undefined}
                aria-label={`Página ${item}`}
              >
                {item}
              </button>
            )
          )}
          <button
            type='button'
            className='logs-page-button'
            onClick={() => onPageChange(page + 1)}
            disabled={disabled || page === pageCount}
            aria-label='Próxima página'
          >
            <ChevronRight size={16} aria-hidden='true' />
          </button>
          <button
            type='button'
            className='logs-page-button'
            onClick={() => onPageChange(pageCount)}
            disabled={disabled || page === pageCount}
            aria-label='Última página'
          >
            <ChevronsRight size={16} aria-hidden='true' />
          </button>
        </nav>
      )}
    </div>
  )
}

export default Pagination
